import React from 'react';
import { SRLWrapper, useLightbox } from 'simple-react-lightbox';
import './image-tile.css';
import './gallery-tile.css';
import ImageTile, { ImageTileBlock } from './image-tile';
import parse from 'html-react-parser';
import { GatsbyImageSharpFluidFragment } from '../../../graphql-types';
import { WPBlock } from '../wp-blocks/types';
import { WpCoreImageBlock } from '../../../generated/graphql';
import { getSrc } from 'gatsby-plugin-image';

interface Image {
  imageFluid: GatsbyImageSharpFluidFragment;
  caption: string;
  alt: string;
  url: string;
  localFullUrl: string;
  localUrl: string;
  src: string;
  thumbnail: string;
}

export interface GalleryTileProps {
  caption?: string;
  images: Image[];
  showThumbnails?: boolean;
  styleClass?: string;
  showImage?: boolean;
  showAll?: boolean;
  columns?: number;
  innerBlocks: WPBlock[];
}

interface CustomImageCaption {
  id: number;
  caption: JSX.Element;
}

const normalizeCaption = (caption) =>
  caption === 'DIE ZAUBERFLÖTE FÜR INKLUSIONSKLASSEN'
    ? 'DIE ZAUBERFLÖTE FÜR INKLUSIONS-KLASSEN'
    : caption;

function generateCustomCaptions(
  allImage: WpCoreImageBlock[],
): CustomImageCaption[] {
  const allImageCustomCaption = [];
  for (let i = 0; i < allImage.length; i++) {
    allImageCustomCaption.push({
      id: i,
      caption: (
        <>
          {parse(
            allImage[i].attributes?.caption
              ?.replace(/'/g, '&apos;')
              .replace(/"/g, '&quot;') ?? '',
          )}
        </>
      ),
    });
  }
  return allImageCustomCaption;
}

function GalleryTile(props: GalleryTileProps): JSX.Element | null {
  const {
    caption,
    images,
    showThumbnails,
    showImage,
    showAll,
    styleClass,
    columns,
    innerBlocks,
  } = props;
  const options = {
    settings: {
      overlayColor: 'rgba(0, 0, 0, 0.7)',
      hideControlsAfter: false,
      disablePanzoom: false,
    },
    thumbnails: {
      showThumbnails: showThumbnails ?? true,
      thumbnailsGap: '0 3px',
    },
    buttons: {
      backgroundColor: '#545082',
      iconColor: '#DEDEE7',
      borderColor: '#DEDEE7',
      showThumbnailsButton: false,
      size: '50px',
    },
    caption: {
      captionColor: 'rgb(255, 255, 255)',
    },
  };
  const imageBlocks = (innerBlocks ?? []).filter(
    (block) => block.__typename === 'WpCoreImageBlock',
  ) as unknown as ImageTileBlock[];

  // console.log('Gallery Tile: image blocks', imageBlocks);
  if (imageBlocks.length === 0) {
    return null;
  }
  const customCaptions: CustomImageCaption[] =
    generateCustomCaptions(imageBlocks);

  const { openLightbox } = useLightbox();

  const styleClasses = 'gallery-tile ' + (styleClass ?? '');

  const mappedImages = imageBlocks.map((image, index) => {
    return {
      src: getSrc(image.attributes?.imageFluid?.childImageSharp?.full),
      thumbnail: getSrc(
        image.attributes?.imageFluid?.childImageSharp?.thumbnail,
      ),
      width: '300px',
      height: 'auto',
    };
  });
  let isSpecialGridLayout = false;
  if (
    styleClass &&
    styleClass.indexOf('gallery-tile--one-line-3-element-big-middle') >= 0
  ) {
    isSpecialGridLayout = true;
  }
  if (showImage && showAll) {
    return (
      <>
        <SRLWrapper options={options} customCaptions={customCaptions}>
          <div className={styleClasses}>
            {imageBlocks.map((imageBlock, index) => {
              const caption = imageBlock.attributes?.caption ?? '&nbsp;';
              return (
                <ImageTile
                  key={index}
                  image={imageBlock as ImageTileBlock}
                  caption={caption} // enforce white space to show arrow icon
                  styleClass={
                    isSpecialGridLayout
                      ? `caption--with-arrow gallery-tile--one-line-3-element-big-middle-${index}`
                      : 'caption--with-arrow'
                  }
                />
              );
            })}
          </div>
        </SRLWrapper>
      </>
    );
  } else {
    const firstImageBlock = imageBlocks[0];
    const actualCaption =
      normalizeCaption(caption || firstImageBlock.attributes?.caption) ||
      '&nbsp;';

    return (
      <div className={styleClasses} onClick={() => openLightbox()}>
        {showImage && (
          <>
            <ImageTile
              image={firstImageBlock as ImageTileBlock}
              caption={actualCaption} // enforce white space to show arrow icon
              styleClass={'caption--with-arrow'}
            />
          </>
        )}
        {!showImage && caption && (
          <div
            className="gallery-tile__no-image-caption"
            dangerouslySetInnerHTML={{ __html: actualCaption }}
          />
        )}
        <SRLWrapper
          options={options}
          images={mappedImages}
          styleClass={'caption--with-arrow'}
          customCaptions={customCaptions}
        />
      </div>
    );
  }
}

export default GalleryTile;
